import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { useAuth, landingPageFor } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, isLoggedIn, landingPage } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await apiCall('/api/auth/member-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        login(data.member, data.token);
        // Land on the member's configured page, else the role-based default.
        navigate(landingPageFor(data.member));
      } else {
        setError(data.error || 'Invalid username or password');
      }
    } catch (err: any) {
      setError('Failed to connect to server. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Already signed in? The login form is a dead end - send them to their page.
  // (Hooks above all run unconditionally, so this early return is safe.)
  if (isLoggedIn) {
    return <Navigate to={landingPage} replace />;
  }

  const inputStyle = (field: string): React.CSSProperties => ({
    width: '100%',
    padding: 12,
    borderRadius: 6,
    border: `1px solid ${focusedField === field ? '#c8850f' : '#5a3a1a'}`,
    background: '#1a0f0a',
    color: '#f5deb3',
    fontSize: 16,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  });

  return (
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'transparent'
    }}>
      <div style={{
        maxWidth: 400,
        width: '100%',
        padding: 32,
        background: '#28190f',
        borderRadius: 12,
        border: '1px solid #5a3a1a',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        <h2 style={{
          color: '#ffd700',
          textAlign: 'center',
          marginBottom: 8,
          fontSize: '1.8rem',
          textShadow: '0 0 10px rgba(255, 215, 0, 0.4), 0 0 20px rgba(255, 215, 0, 0.2)'
        }}>
          Welcome Back
        </h2>
        <p style={{
          color: '#c8a060',
          textAlign: 'center',
          marginBottom: 24
        }}>
          Sign in to access your dashboard
        </p>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: '#c8a060', marginBottom: 8 }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onFocus={() => setFocusedField('username')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter your username"
              autoComplete="username"
              required
              style={inputStyle('username')}
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', color: '#c8a060', marginBottom: 8 }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              style={inputStyle('password')}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 6,
              border: loading ? '1px solid #666' : '1px solid #d4960f',
              background: loading ? '#666' : 'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
              color: '#f5deb3',
              fontSize: 18,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {error && (
          <p style={{
            color: '#e74c3c',
            textAlign: 'center',
            marginTop: 16
          }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
